/**
 * Feather icon adapter for legacy MUI icon call sites.
 *
 * This keeps older icon call sites working while the UI moves to native
 * industrial primitives.
 */
import React from 'react';
import type { Icon, IconProps } from 'react-feather';
import {
    Activity,
    AlertCircle,
    AlertTriangle,
    ArrowUp,
    BookOpen,
    Camera,
    CheckCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Clock,
    Cloud,
    Code,
    Command,
    Copy,
    Cpu,
    Crosshair,
    Database,
    Download,
    Edit3,
    ExternalLink,
    File,
    FileText,
    Folder,
    Globe,
    Grid,
    HelpCircle,
    Image,
    Info,
    Key,
    Layers,
    Link,
    List,
    LogOut,
    Mail,
    MapPin,
    Menu,
    MessageCircle,
    Mic,
    Monitor,
    MinusCircle,
    MoreVertical,
    Paperclip,
    Phone,
    Play,
    Plus,
    PlusCircle,
    RefreshCw,
    RotateCcw,
    Save,
    Search,
    Send,
    Server,
    Settings,
    Share2,
    Shield,
    Square,
    Trash2,
    Unlock,
    Upload,
    UploadCloud,
    User,
    UserPlus,
    Users,
    Eye,
    EyeOff,
    X,
    ZoomIn,
    ZoomOut
} from 'react-feather';

type LegacyIconColor =
    | IconProps['color']
    | 'inherit'
    | 'action'
    | 'disabled'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';

type LegacyIconSize = 'inherit' | 'small' | 'medium' | 'large' | number | string;
type LegacySxProps = Record<string, unknown> | Array<Record<string, unknown>> | undefined;

type FeatherIconProps = Omit<IconProps, 'color' | 'size'> & {
    color?: LegacyIconColor;
    fontSize?: LegacyIconSize;
    htmlColor?: string;
    sx?: LegacySxProps;
    titleAccess?: string;
};

const colorTokens: Record<string, string> = {
    inherit: 'currentColor',
    action: 'currentColor',
    disabled: '#94a3b8',
    primary: '#22d3ee',
    secondary: '#94a3b8',
    error: '#ef4444',
    info: '#38bdf8',
    success: '#34d399',
    warning: '#f59e0b'
};

const themeColorTokens: Record<string, string> = {
    'primary.main': '#22d3ee',
    'secondary.main': '#94a3b8',
    'error.main': '#ef4444',
    'info.main': '#38bdf8',
    'success.main': '#34d399',
    'warning.main': '#f59e0b'
};

const sizeTokens: Record<string, number | string> = {
    inherit: '1em',
    small: 18,
    medium: 22,
    large: 28
};

const getSxFontSize = (sx?: LegacySxProps): number | string | undefined => {
    if (!sx || Array.isArray(sx) || typeof sx !== 'object') {
        return undefined;
    }

    const fontSize = (sx as Record<string, unknown>).fontSize;
    if (typeof fontSize === 'number' || typeof fontSize === 'string') {
        return fontSize;
    }

    return undefined;
};

const spacingValue = (value: unknown) => {
    if (typeof value === 'number') {
        return `${value * 8}px`;
    }

    if (typeof value === 'string') {
        return value;
    }

    return undefined;
};

const sxToStyle = (sx?: LegacySxProps): React.CSSProperties | undefined => {
    if (!sx || Array.isArray(sx) || typeof sx !== 'object') {
        return undefined;
    }

    const sxRecord = sx as Record<string, unknown>;
    const style: React.CSSProperties = {};

    const marginRight = spacingValue(sxRecord.mr ?? sxRecord.marginRight);
    if (marginRight) {
        style.marginRight = marginRight;
    }

    const marginLeft = spacingValue(sxRecord.ml ?? sxRecord.marginLeft);
    if (marginLeft) {
        style.marginLeft = marginLeft;
    }

    const marginTop = spacingValue(sxRecord.mt ?? sxRecord.marginTop);
    if (marginTop) {
        style.marginTop = marginTop;
    }

    const marginBottom = spacingValue(sxRecord.mb ?? sxRecord.marginBottom);
    if (marginBottom) {
        style.marginBottom = marginBottom;
    }

    if (typeof sxRecord.color === 'string') {
        style.color = themeColorTokens[sxRecord.color] ?? sxRecord.color;
    }

    if (typeof sxRecord.transform === 'string') {
        style.transform = sxRecord.transform;
    }

    return Object.keys(style).length > 0 ? style : undefined;
};

const resolveSize = (fontSize: LegacyIconSize | undefined, sx?: LegacySxProps) => {
    const sxFontSize = getSxFontSize(sx);
    if (sxFontSize !== undefined) {
        return sxFontSize;
    }

    if (fontSize === undefined) {
        return sizeTokens.medium;
    }

    return sizeTokens[String(fontSize)] ?? fontSize;
};

const resolveColor = (color?: LegacyIconColor, htmlColor?: string) => {
    if (htmlColor) {
        return htmlColor;
    }

    if (!color) {
        return 'currentColor';
    }

    return colorTokens[String(color)] ?? String(color);
};

const createFeatherIcon = (IconComponent: Icon) => {
    const FeatherIcon = ({
        color,
        fontSize,
        htmlColor,
        strokeWidth = 2,
        style,
        sx,
        titleAccess,
        ...props
    }: FeatherIconProps) => (
        <IconComponent
            aria-hidden={titleAccess ? undefined : true}
            aria-label={titleAccess}
            role={titleAccess ? 'img' : undefined}
            size={resolveSize(fontSize, sx)}
            color={resolveColor(color, htmlColor)}
            strokeWidth={strokeWidth}
            style={{ ...sxToStyle(sx), ...style }}
            {...props}
        />
    );

    FeatherIcon.displayName = `Feather${IconComponent.displayName || IconComponent.name || 'Icon'}`;
    return FeatherIcon;
};

export const AccountCircleIcon = createFeatherIcon(User);
export const ActivityIcon = createFeatherIcon(Activity);
export const AddIcon = createFeatherIcon(PlusCircle);
export const PlusIcon = createFeatherIcon(Plus);
export const ErrorIcon = createFeatherIcon(AlertCircle);
export const ArrowBackIcon = createFeatherIcon(ChevronLeft);
export const AttachFileIcon = createFeatherIcon(Paperclip);
export const BugReportIcon = createFeatherIcon(Cpu);
export const ChatBubbleIcon = createFeatherIcon(MessageCircle);
export const CheckCircleIcon = createFeatherIcon(CheckCircle);
export const ClearIcon = createFeatherIcon(X);
export const ChevronLeftIcon = createFeatherIcon(ChevronLeft);
export const ChevronRightIcon = createFeatherIcon(ChevronRight);
export const CloseIcon = createFeatherIcon(X);
export const CloudUploadIcon = createFeatherIcon(UploadCloud);
export const CloudIcon = createFeatherIcon(Cloud);
export const CodeIcon = createFeatherIcon(Code);
export const ContentCopyIcon = createFeatherIcon(Copy);
export const CrosshairIcon = createFeatherIcon(Crosshair);
export const DashboardIcon = createFeatherIcon(Grid);
export const DatabaseIcon = createFeatherIcon(Database);
export const DeleteIcon = createFeatherIcon(Trash2);
export const DeleteSweepIcon = createFeatherIcon(Trash2);
export const DescriptionIcon = createFeatherIcon(FileText);
export const DownloadIcon = createFeatherIcon(Download);
export const EditIcon = createFeatherIcon(Edit3);
export const EmailIcon = createFeatherIcon(Mail);
export const ExpandLessIcon = createFeatherIcon(ChevronUp);
export const ExpandMoreIcon = createFeatherIcon(ChevronDown);
export const FetchIcon = createFeatherIcon(Download);
export const FileIcon = createFeatherIcon(FileText);
export const FilterIcon = createFeatherIcon(Search);
export const GlobeIcon = createFeatherIcon(Globe);
export const FolderIcon = createFeatherIcon(Folder);
export const FolderOpenIcon = createFeatherIcon(Folder);
export const HelpIcon = createFeatherIcon(HelpCircle);
export const HistoryIcon = createFeatherIcon(Clock);
export const InfoIcon = createFeatherIcon(Info);
export const ProcessingIcon = createFeatherIcon(Clock);
export const InsertDriveFileIcon = createFeatherIcon(File);
export const KeyboardArrowDownIcon = createFeatherIcon(ChevronDown);
export const KeyboardArrowLeftIcon = createFeatherIcon(ChevronLeft);
export const KeyboardArrowRightIcon = createFeatherIcon(ChevronRight);
export const KeyboardIcon = createFeatherIcon(Command);
export const LayersIcon = createFeatherIcon(Layers);
export const ListIcon = createFeatherIcon(List);
export const LockResetIcon = createFeatherIcon(Unlock);
export const LogoutIcon = createFeatherIcon(LogOut);
export const MenuIcon = createFeatherIcon(Menu);
export const MonitorIcon = createFeatherIcon(Monitor);
export const MicIcon = createFeatherIcon(Mic);
export const MoreVertIcon = createFeatherIcon(MoreVertical);
export const OpenInNewIcon = createFeatherIcon(ExternalLink);
export const PeopleIcon = createFeatherIcon(Users);
export const PersonAddIcon = createFeatherIcon(UserPlus);
export const PhoneIcon = createFeatherIcon(Phone);
export const PlayArrowIcon = createFeatherIcon(Play);
export const PushPinIcon = createFeatherIcon(MapPin);
export const PushPinOutlinedIcon = createFeatherIcon(MapPin);
export const RefreshIcon = createFeatherIcon(RefreshCw);
export const RemoveCircleOutlineIcon = createFeatherIcon(MinusCircle);
export const RestartAltIcon = createFeatherIcon(RotateCcw);
export const SchoolIcon = createFeatherIcon(BookOpen);
export const SearchIcon = createFeatherIcon(Search);
export const SendIcon = createFeatherIcon(Send);
export const ArrowUpIcon = createFeatherIcon(ArrowUp);
export const ServerIcon = createFeatherIcon(Server);
export const SettingsIcon = createFeatherIcon(Settings);
export const SaveIcon = createFeatherIcon(Save);
export const ScienceIcon = createFeatherIcon(Cpu);
export const ShareIcon = createFeatherIcon(Share2);
export const ShieldIcon = createFeatherIcon(Shield);
export const SmartToyIcon = createFeatherIcon(Cpu);
export const StopIcon = createFeatherIcon(Square);
export const UploadIcon = createFeatherIcon(Upload);
export const LinkIcon = createFeatherIcon(Link);
export const SourceIcon = createFeatherIcon(FileText);
export const PersonIcon = createFeatherIcon(User);
export const BotIcon = createFeatherIcon(Cpu);
export const BuildIcon = createFeatherIcon(Settings);
export const CameraIcon = createFeatherIcon(Camera);
export const ImageIcon = createFeatherIcon(Image);
export const VpnKeyIcon = createFeatherIcon(Key);
export const WarningIcon = createFeatherIcon(AlertTriangle);
export const VisibilityIcon = createFeatherIcon(Eye);
export const VisibilityOffIcon = createFeatherIcon(EyeOff);
export const ZoomInIcon = createFeatherIcon(ZoomIn);
export const ZoomOutIcon = createFeatherIcon(ZoomOut);
